import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const PRIMARY = '#FF6B00';
const SECONDARY = '#7B61FF';
const ACCENT = '#00E5FF';

const fadeRange = (
  frame: number,
  inStart: number,
  inEnd: number,
  outStart: number,
  outEnd: number,
) => {
  const a = interpolate(frame, [inStart, inEnd], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const b = interpolate(frame, [outStart, outEnd], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return a * b;
};

const phaseOpacity = (frame: number, start: number, peakIn: number, peakOut: number, end: number) =>
  fadeRange(frame, start, peakIn, peakOut, end);

const StarFieldLayer: React.FC<{ density: number; seed: number; opacity: number }> = ({
  density,
  seed,
  opacity,
}) => {
  const frame = useCurrentFrame();
  const stars = React.useMemo(() => {
    const out: { x: number; y: number; s: number; b: number; sp: number }[] = [];
    for (let i = 0; i < density; i++) {
      const h1 = ((i * 7919 + seed) % 100000) / 100000;
      const h2 = ((i * 6271 + seed * 3) % 100000) / 100000;
      const h3 = ((i * 3571 + seed * 7) % 100000) / 100000;
      out.push({
        x: h1 * 1920,
        y: h2 * 1080,
        s: 0.8 + h3 * 2.2,
        b: 0.15 + h3 * 0.85,
        sp: 0.015 + h1 * 0.05,
      });
    }
    return out;
  }, [density, seed]);

  return (
    <div style={{ position: 'absolute', inset: 0, opacity, pointerEvents: 'none' }}>
      {stars.map((st, i) => {
        const tw = 0.55 + 0.45 * Math.sin(frame * st.sp + i + seed);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: st.x,
              top: st.y,
              width: st.s,
              height: st.s,
              borderRadius: '50%',
              background: `rgba(255,255,255,${st.b * tw})`,
            }}
          />
        );
      })}
    </div>
  );
};

const GravitationalWell: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => {
  const wobble = Math.sin(frame * 0.04) * 4;
  return (
    <svg
      style={{ position: 'absolute', left: '62%', top: 520, transform: 'translate(-50%, -50%)', opacity }}
      width="900"
      height="700"
      viewBox="0 0 900 700"
    >
      <defs>
        <radialGradient id="wellGrad">
          <stop offset="0%" stopColor="#000000" />
          <stop offset="35%" stopColor="#1a0a20" />
          <stop offset="70%" stopColor={SECONDARY} stopOpacity="0.15" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0.08" />
        </radialGradient>
      </defs>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const ry = 80 + i * 48 + wobble * (0.1 * i);
        const rx = 200 + i * 72;
        const rot = frame * (0.15 + i * 0.04);
        return (
          <ellipse
            key={i}
            cx={450}
            cy={340}
            rx={rx}
            ry={ry}
            fill="none"
            stroke={i % 2 === 0 ? ACCENT : SECONDARY}
            strokeOpacity={0.12 + i * 0.06}
            strokeWidth={2}
            transform={`rotate(${rot} 450 340)`}
          />
        );
      })}
      <ellipse cx={450} cy={340} rx={280} ry={140} fill="url(#wellGrad)" opacity={0.55} />
    </svg>
  );
};

const EventHorizonScene: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const p = spring({ frame: frame - 132, fps, config: { damping: 14, stiffness: 100 } });
  const pulse = 1 + Math.sin(frame * 0.08) * 0.015;
  const glowPulse = 0.6 + Math.sin(frame * 0.06) * 0.15;

  const cx = 980;
  const cy = 560;
  const ehRadius = 120;

  const lightRays = [
    { startX: 1650, startY: 260, bend: 0.3, escapes: true },
    { startX: 1680, startY: 400, bend: 0.6, escapes: true },
    { startX: 1700, startY: 520, bend: 1.0, escapes: false },
    { startX: 1680, startY: 640, bend: 0.8, escapes: false },
    { startX: 1650, startY: 760, bend: 0.5, escapes: true },
    { startX: 1650, startY: 880, bend: 0.3, escapes: true },
  ];

  return (
    <svg
      style={{ position: 'absolute', inset: 0, opacity: opacity * p }}
      viewBox="0 0 1920 1080"
    >
      <defs>
        <radialGradient id="bhGlow">
          <stop offset="0%" stopColor="#000" />
          <stop offset="60%" stopColor="#000" />
          <stop offset="80%" stopColor={SECONDARY} stopOpacity="0.25" />
          <stop offset="95%" stopColor={PRIMARY} stopOpacity="0.12" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ehGlow">
          <stop offset="0%" stopColor={ACCENT} stopOpacity="0" />
          <stop offset="70%" stopColor={ACCENT} stopOpacity="0" />
          <stop offset="85%" stopColor={ACCENT} stopOpacity={`${glowPulse}`} />
          <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
        </radialGradient>
        <marker id="arrowHead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <path d="M0,0 L8,3 L0,6" fill={ACCENT} fillOpacity="0.7" />
        </marker>
        <marker id="arrowRed" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <path d="M0,0 L8,3 L0,6" fill={PRIMARY} fillOpacity="0.7" />
        </marker>
      </defs>

      <circle cx={cx} cy={cy} r={ehRadius * 2.5} fill="url(#bhGlow)" />
      <circle cx={cx} cy={cy} r={ehRadius * 1.8} fill="url(#ehGlow)" opacity={pulse} />

      <circle cx={cx} cy={cy} r={ehRadius * 0.55} fill="#020208" />

      <circle
        cx={cx} cy={cy} r={ehRadius}
        fill="none"
        stroke={ACCENT}
        strokeWidth={2.5}
        strokeDasharray="12 6"
        strokeDashoffset={-frame * 0.6}
        opacity={0.85}
      />

      {lightRays.map((ray, i) => {
        const dy = ray.startY - cy;
        const dashOffset = -frame * (2 + i * 0.5);

        if (ray.escapes) {
          const passY = cy + dy * 0.35;
          const endX = cx - 350 - Math.abs(dy) * 0.25;
          const endY = cy + dy * 1.4;
          const c1x = cx + 280;
          const c1y = ray.startY;
          const c2x = cx + 150;
          const c2y = passY;
          const d = `M${ray.startX},${ray.startY} C${c1x},${c1y} ${c2x},${c2y} ${endX},${endY}`;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={ACCENT}
              strokeWidth={2}
              strokeDasharray="10 8"
              strokeDashoffset={dashOffset}
              opacity={0.55}
              markerEnd="url(#arrowHead)"
            />
          );
        }

        const endX = cx + (dy > 0 ? -15 : 15);
        const endY = cy + (dy > 0 ? 20 : -20);
        const c1x = cx + 320;
        const c1y = ray.startY;
        const c2x = cx + ehRadius * 0.6;
        const c2y = cy + dy * 0.35;
        const d = `M${ray.startX},${ray.startY} C${c1x},${c1y} ${c2x},${c2y} ${endX},${endY}`;
        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={PRIMARY}
            strokeWidth={2}
            strokeDasharray="8 6"
            strokeDashoffset={dashOffset}
            opacity={0.65}
            markerEnd="url(#arrowRed)"
          />
        );
      })}

      <text x={cx} y={cy - ehRadius - 20} textAnchor="middle" fill={ACCENT} fontSize="20" fontWeight="700" opacity="0.9">
        事件视界
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="16" fontWeight="600">
        奇点
      </text>

      <text x={cx - ehRadius - 40} y={cy - ehRadius - 40} textAnchor="end" fill={ACCENT} fontSize="16" fontWeight="600" opacity="0.7">
        ← 光线弯曲后逃逸
      </text>
      <text x={cx - ehRadius - 40} y={cy + ehRadius + 50} textAnchor="end" fill={PRIMARY} fontSize="16" fontWeight="600" opacity="0.7">
        光线被捕获 ✕
      </text>
    </svg>
  );
};

const AccretionDisk: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => {
  const rot = frame * 1.2;
  return (
    <svg
      style={{ position: 'absolute', left: '60%', top: 450, transform: 'translate(-50%, -50%)', opacity }}
      width="820"
      height="820"
      viewBox="0 0 820 820"
    >
      <defs>
        <linearGradient id="diskGrad" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.95" />
          <stop offset="25%" stopColor="#FFAA44" stopOpacity="0.8" />
          <stop offset="50%" stopColor={SECONDARY} stopOpacity="0.75" />
          <stop offset="75%" stopColor={ACCENT} stopOpacity="0.7" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <g transform={`rotate(${rot} 410 410)`}>
        <ellipse
          cx={410}
          cy={410}
          rx={360}
          ry={95}
          fill="none"
          stroke="url(#diskGrad)"
          strokeWidth={36}
          opacity={0.85}
        />
        <ellipse
          cx={410}
          cy={410}
          rx={300}
          ry={78}
          fill="none"
          stroke={ACCENT}
          strokeWidth={8}
          strokeOpacity={0.35}
        />
      </g>
      <ellipse cx={410} cy={410} rx={55} ry={55} fill="#030308" stroke={SECONDARY} strokeOpacity="0.5" strokeWidth="2" />
    </svg>
  );
};

const HawkingParticles: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => {
  const parts = React.useMemo(() => {
    const out: { ang: number; dist: number; spd: number; hue: number }[] = [];
    for (let i = 0; i < 42; i++) {
      const h = ((i * 4999 + 13337) % 100000) / 100000;
      out.push({
        ang: h * Math.PI * 2,
        dist: 130 + (i % 7) * 18,
        spd: 0.02 + (h * 0.04),
        hue: i % 3,
      });
    }
    return out;
  }, []);

  return (
    <svg style={{ position: 'absolute', left: '50%', top: 500, transform: 'translate(-50%,-50%)', opacity }} width="600" height="600" viewBox="0 0 600 600">
      {parts.map((p, i) => {
        const a = p.ang + frame * p.spd;
        const r = p.dist + Math.sin(frame * 0.08 + i) * 10;
        const x = 300 + Math.cos(a) * r;
        const y = 300 + Math.sin(a) * r * 0.55;
        const c = p.hue === 0 ? ACCENT : p.hue === 1 ? SECONDARY : PRIMARY;
        return (
          <circle key={i} cx={x} cy={y} r={2 + (i % 3)} fill={c} opacity={0.5 + (i % 5) * 0.1} />
        );
      })}
      <text x={300} y={520} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="28" fontWeight="700">
        霍金辐射（示意）：视界附近量子涨落产生粒子对
      </text>
    </svg>
  );
};

const SummaryFacts: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const facts = [
    { k: '奇点', v: '经典理论中密度趋于无穷（量子引力待解释）' },
    { k: '不可见', v: '本身不发光；通过引力与周围物质间接探测' },
    { k: '时间延缓', v: '靠近视界，外界看来时间几乎停滞' },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        left: 80,
        right: 80,
        bottom: 90,
        opacity,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 20,
      }}
    >
      {facts.map((f, i) => {
        const sp = spring({
          frame: frame - 410 - i * 12,
          fps,
          config: { damping: 14, stiffness: 160 },
        });
        return (
          <div
            key={i}
            style={{
              padding: '20px 22px',
              borderRadius: 14,
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${i === 1 ? SECONDARY : PRIMARY}44`,
              opacity: sp,
              transform: `translateY(${(1 - sp) * 24}px)`,
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 800, color: ACCENT }}>{f.k}</div>
            <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.72)', marginTop: 8, lineHeight: 1.45 }}>
              {f.v}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const BlackHolePopSciAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleP = spring({ frame: frame - 3, fps, config: { damping: 12, stiffness: 200 } });
  const titleY = interpolate(titleP, [0, 1], [-36, 0]);

  // 4 pages with 12-frame crossfade overlaps
  const opTitle = phaseOpacity(frame, 0, 12, 120, 140);
  const opHorizon = phaseOpacity(frame, 128, 140, 248, 268);
  const opDisk = phaseOpacity(frame, 256, 268, 376, 396);
  const opHawking = phaseOpacity(frame, 384, 396, 520, 540);

  const bgIn = spring({ frame, fps, config: { damping: 16, stiffness: 100 } });

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 50% 35%, #1a0d28 0%, #08090c 50%, #020205 100%)',
        fontFamily: 'system-ui, -apple-system, "PingFang SC", sans-serif',
        overflow: 'hidden',
      }}
    >
      <StarFieldLayer density={200} seed={101} opacity={bgIn * 0.9} />
      <StarFieldLayer density={90} seed={303} opacity={bgIn * 0.45} />

      <div style={{ opacity: opTitle }}>
        <GravitationalWell frame={frame} opacity={0.95} />
      </div>

      <div
        style={{
          position: 'absolute',
          top: 42,
          left: 56,
          opacity: opTitle * titleP,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 62,
            fontWeight: 900,
            margin: 0,
            background: `linear-gradient(110deg, ${PRIMARY}, ${ACCENT}, ${SECONDARY})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          黑洞揭秘
        </h1>
        <p style={{ fontSize: 28, margin: '10px 0 0', color: 'rgba(255,255,255,0.42)' }}>
          宇宙最神秘天体 · 引力、视界与辐射
        </p>
        <h3 style={{ margin: '150px 0 0', color: ACCENT, fontSize: 34, fontWeight: 800 }}>什么是黑洞？</h3>
        <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.78)', lineHeight: 1.6, marginTop: 14, maxWidth: 600 }}>
          极大质量集中在极小区域内，引力强到连光也无法逃逸。史瓦西半径 R<sub>s</sub> = 2GM/c²
          刻画了事件视界的尺度。
        </p>
        <div
          style={{
            marginTop: 18,
            padding: 16,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${SECONDARY}22, ${PRIMARY}18)`,
            fontSize: 26,
            color: ACCENT,
            fontWeight: 600,
            maxWidth: 600,
          }}
        >
          不是"洞"，而是时空极度弯曲的区域；外部观测者看到吸积盘高热发光。
        </div>
      </div>

      <div style={{ opacity: opHorizon }}>
        <EventHorizonScene frame={frame} fps={fps} opacity={1} />
        <div
          style={{
            position: 'absolute',
            left: 72,
            top: 320,
            width: 480,
            opacity: spring({ frame: frame - 138, fps, config: { damping: 14, stiffness: 120 } }),
            padding: 22,
            background: 'rgba(15,10,30,0.75)',
            borderRadius: 14,
            border: `1px solid ${SECONDARY}44`,
          }}
        >
          <h3 style={{ margin: 0, color: PRIMARY, fontSize: 32, fontWeight: 800 }}>事件视界</h3>
          <p style={{ margin: '12px 0 0', fontSize: 26, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55 }}>
            一旦越过这一边界，任何路径都指向中心；对外部观测者，落入物体发出的光无限红移。
          </p>
        </div>
      </div>

      <div style={{ opacity: opDisk }}>
        <AccretionDisk frame={frame} opacity={1} />
        <div
          style={{
            position: 'absolute',
            left: 72,
            top: 300,
            width: 520,
            padding: 24,
            borderRadius: 16,
            background: 'rgba(5,5,12,0.88)',
            border: `1px solid ${PRIMARY}50`,
            opacity: spring({ frame: frame - 266, fps, config: { damping: 14, stiffness: 100 } }),
          }}
        >
          <h3 style={{ margin: 0, color: ACCENT, fontSize: 32, fontWeight: 800 }}>吸积盘</h3>
          <p style={{ margin: '12px 0 0', fontSize: 26, color: 'rgba(255,255,255,0.74)', lineHeight: 1.55 }}>
            气体在角动量作用下形成旋转薄盘，摩擦与压缩加热至百万度，辐射 X 射线与可见光——这是人类"看到"黑洞的主要方式。
          </p>
        </div>
      </div>

      <div style={{ opacity: opHawking }}>
        <HawkingParticles frame={frame} opacity={1} />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: 120,
            maxWidth: 920,
            textAlign: 'center',
            opacity: spring({ frame: frame - 392, fps, config: { damping: 16, stiffness: 90 } }),
            fontSize: 28,
            color: 'rgba(255,255,255,0.68)',
            lineHeight: 1.5,
          }}
        >
          霍金辐射预言黑洞并非完全"黑"，会以极低温热辐射缓慢损失质量；对恒星质量黑洞，效应极其微弱。
        </div>
        <SummaryFacts frame={frame} fps={fps} opacity={1} />
      </div>

      <div style={{ position: 'absolute', bottom: 28, right: 56, fontSize: 24, color: 'rgba(255,255,255,0.25)' }}>
        硬核科普 · 通识教育
      </div>
    </AbsoluteFill>
  );
};
